import { useState, useContext } from 'react';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Chip,
} from '@mui/material';
import {
    Language as LanguageIcon,
    Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../sporttia/all';
import generalConfig from '../config/general';

type Language = (typeof generalConfig)['languages'][number];

const FLAG_CODES: Record<string, string> = Object.fromEntries(
    generalConfig.languages.map((lg) => [lg.key, lg.flag]),
);

/**
 * Bandera de flag-icons. La hoja de estilos se carga por CDN desde index.html,
 * igual que en sporttia-competitions.
 */
function Flag({
    languageKey,
    className,
}: {
    languageKey: string;
    className?: string;
}): React.ReactElement | null {
    const code = FLAG_CODES[languageKey];

    if (!code) {
        return null;
    }

    return <span className={`fi fi-${code} ${className ?? ''}`} />;
}

export default function LanguageSelector(): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    if (!cxt) {
        return null;
    }

    const openDialog = (): void => {
        setDialogOpen(true);
    };

    const selectLanguage = (lg: Language): void => {
        cxt.setLang(lg);
        setDialogOpen(false);

        // If user is logged in, persist language preference to backend
        if (cxt.logged) {
            cxt.api('PUT', '/me', {
                params: {
                    lang: lg.key,
                },
            });
        }
    };

    return (
        <>
            {cxt.lang && (
                <Chip
                    icon={<LanguageIcon sx={{ fontSize: 18 }} />}
                    label={
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Flag
                                languageKey={cxt.lang.key}
                                className="text-base"
                            />
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                            >
                                {cxt.lang.key.toUpperCase()}
                            </Typography>
                        </Box>
                    }
                    onClick={openDialog}
                    variant="outlined"
                    sx={{
                        cursor: 'pointer',
                        px: 0.5,
                        '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer',
                        },
                    }}
                />
            )}

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                PaperProps={{
                    sx: { minWidth: 280, borderRadius: 2 },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LanguageIcon color="primary" />
                        <Typography variant="h6">
                            {t('general.language')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <List sx={{ pt: 0 }}>
                    {cxt.config &&
                        cxt.config.languages.map((lg: any) => (
                            <ListItemButton
                                key={lg.key}
                                onClick={() => selectLanguage(lg)}
                                selected={cxt.lang?.key === lg.key}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { cursor: 'pointer' },
                                    py: 1.5,
                                    px: 3,
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <Flag
                                        languageKey={lg.key}
                                        className="text-2xl"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={lg.caption}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                {cxt.lang?.key === lg.key && (
                                    <CheckIcon color="primary" />
                                )}
                            </ListItemButton>
                        ))}
                </List>
            </Dialog>
        </>
    );
}
